import {
  directThreadCatchUpAfterReconnect,
  markOtherHydratedDirectThreadsHasMoreNewer,
} from '@/components/Messenger/cache/directThreadCacheStore';
import { setMessengerDraftsSnapshot } from '@/components/Messenger/cache/messengerDraftsStore';
import {
  refreshAgentChatAfterReconnect,
  syncAgentChatsProcessingAfterReconnect,
} from '@/components/Messenger/cache/agentChatReconnectReconcile';
import { flushMessengerDeliveryAcks } from '@/components/Messenger/cache/messengerDeliveryAck';
import {
  emitMessengerAgentChatRecover,
  invalidateMessengerAgentChats,
  invalidateMessengerUnread,
} from '@/constants/messengerEvents';
import { getSocket } from '@/hooks/useRealtimeSync';
import { api } from '@/api/client';
import { getMessengerNotificationSurfaceState } from '@/notifications/messengerSurfaceState';
import { dismissNotificationsForReadEntities } from '@/notifications/notificationOrchestrator';

let installed = false;

const RECONCILE_DEBOUNCE_MS = 300;
/** Enough agent chats to match typical open notifications after offline read elsewhere. */
const AGENT_CHATS_DISMISS_LIST_LIMIT = 100;

/**
 * On socket reconnect / visibility: flush delivery acks, refresh unread,
 * force catch-up for the open DM, recover open agent chat, and mark other
 * hydrated DMs as not-on-live-tip.
 * Thread list refresh is handled by ChatShell via MESSENGER_UNREAD_INVALIDATE.
 * Also dismisses OS notifications for threads/chats already read on the server.
 */
export function installMessengerReconnectReconcile(): () => void {
  if (installed) return () => undefined;
  installed = true;

  const socket = getSocket();
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  let pending = false;

  const dismissStaleNotifications = async () => {
    try {
      const [threadsRes, agentRes] = await Promise.all([
        api.messenger.getThreads(),
        api.agentChats.list({ limit: AGENT_CHATS_DISMISS_LIST_LIMIT }),
      ]);
      dismissNotificationsForReadEntities({
        threads: threadsRes.threads,
        agentChats: agentRes.chats,
      });
    } catch {
      /* dismiss is best-effort; list refresh still proceeds via invalidate */
    }
  };

  const runOnce = async () => {
    flushMessengerDeliveryAcks();
    invalidateMessengerUnread();

    const surface = getMessengerNotificationSurfaceState();
    const activeThreadId = surface.activeThreadId?.trim() || null;
    const activeAgentChatId = surface.activeAgentChatId?.trim() || null;

    markOtherHydratedDirectThreadsHasMoreNewer(activeThreadId);

    const tasks: Promise<unknown>[] = [];

    tasks.push(dismissStaleNotifications());
    tasks.push(
      api.messenger
        .getDrafts()
        .then((response) => {
          setMessengerDraftsSnapshot(response.drafts);
        })
        .catch(() => {
          /* keep the current draft snapshot until the next reconnect */
        })
    );

    if (activeThreadId) {
      tasks.push(directThreadCatchUpAfterReconnect(activeThreadId));
    }

    if (activeAgentChatId) {
      tasks.push(
        refreshAgentChatAfterReconnect(activeAgentChatId)
          .then((result) => {
            if (!result) return;
            emitMessengerAgentChatRecover({
              chatId: activeAgentChatId,
              processing: result.processing,
            });
          })
          .catch(() => {
            /* keep previous agent UI; next socket event / manual refresh can recover */
          })
      );
    }

    tasks.push(
      syncAgentChatsProcessingAfterReconnect()
        .then(() => {
          invalidateMessengerAgentChats();
        })
        .catch(() => {
          /* sidebar processing may stay briefly stale */
        })
    );

    await Promise.all(tasks);
  };

  const reconcile = async () => {
    if (running) {
      pending = true;
      return;
    }
    running = true;
    try {
      do {
        pending = false;
        await runOnce();
      } while (pending);
    } finally {
      running = false;
    }
  };

  const scheduleReconcile = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      void reconcile();
    }, RECONCILE_DEBOUNCE_MS);
  };

  const onConnect = () => {
    scheduleReconcile();
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      scheduleReconcile();
    }
  };

  socket.on('connect', onConnect);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    installed = false;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    socket.off('connect', onConnect);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
